import React from 'react';
import { Box, Typography, IconButton, Modal, Chip, useMediaQuery } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StreamIcon from '@mui/icons-material/Stream';
import LanguageIcon from '@mui/icons-material/Language';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import { useTheme } from '@mui/material/styles';
import { scaleIn, customScrollbar } from '../../styles/styledComponents';

const EventDetailModal = ({ selectedEvent, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const formatTime = (timeStr) => {
    return timeStr.split(' - ').map(time => 
      time.replace(/(\d+):(\d+)/, (_, h, m) => `${h.padStart(2, '0')}:${m}`)
    ).join(' - ');
  };

  return (
    <Modal
      open={!!selectedEvent}
      onClose={onClose}
      closeAfterTransition
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1300,
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
          }
        }
      }}
    >
      <AnimatePresence>
        {selectedEvent && (
          <Box
            component={motion.div}
            {...scaleIn}
            sx={{
              position: 'relative',
              width: isMobile ? '100%' : '90%',
              maxWidth: isMobile ? '100%' : '800px',
              height: isMobile ? '100%' : '90vh',
              maxHeight: isMobile ? '100%' : '90vh',
              overflow: 'hidden',
              backgroundColor: 'rgba(30, 30, 38, 0.98)',
              borderRadius: isMobile ? 0 : '8px',
              boxShadow: '0 0 40px rgba(0, 255, 255, 0.2)',
              display: 'flex',
              flexDirection: 'column',
              border: `1px solid rgba(0, 255, 255, 0.3)`,
              '&:focus': {
                outline: 'none',
              },
              [theme.breakpoints.down('sm')]: {
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                maxHeight: '100%',
                maxWidth: '100%',
                borderRadius: 0,
                border: 'none',
              },
            }}
          >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box 
                sx={{ 
                  position: 'sticky',
                  top: 0,
                  bgcolor: 'rgba(30, 30, 38, 0.98)',
                  borderBottom: `1px solid rgba(0, 255, 255, 0.2)`,
                  zIndex: 1,
                  p: isMobile ? 2 : 3,
                  background: 'linear-gradient(135deg, rgba(30, 30, 38, 0.98) 0%, rgba(25, 25, 32, 0.98) 100%)',
                  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h4" component="h2" sx={{ 
                      fontSize: isMobile ? '1.4rem' : '1.8rem', 
                      letterSpacing: '0.05em', 
                      fontWeight: 800, 
                      color: theme.palette.neon.cyan,
                      mb: 1,
                      textTransform: 'uppercase',
                      textShadow: `0 0 20px ${theme.palette.neon.cyan}`,
                    }}>
                      {selectedEvent.tournamentName || selectedEvent.title}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        icon={<SportsEsportsIcon />}
                        label={selectedEvent.category}
                        sx={{ 
                          fontWeight: 700,
                          backgroundColor: 'rgba(255, 0, 255, 0.1)',
                          borderColor: theme.palette.neon.magenta,
                          color: theme.palette.neon.magenta,
                          border: '1px solid',
                          '& .MuiChip-icon': {
                            color: theme.palette.neon.magenta,
                          }
                        }}
                        size="small"
                      />
                    </Box>
                  </Box>
                  <IconButton
                    sx={{
                      backgroundColor: 'rgba(255, 0, 0, 0.1)',
                      border: `2px solid ${theme.palette.neon.pink}`,
                      color: theme.palette.neon.pink,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 0, 0, 0.2)',
                        transform: 'rotate(90deg) scale(1.1)',
                        boxShadow: `0 0 20px ${theme.palette.neon.pink}`,
                      },
                      transition: 'all 0.3s ease',
                    }}
                    onClick={onClose}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>

              <Box sx={{ 
                flex: 1, 
                overflowY: 'auto',
                overflowX: 'hidden',
                p: isMobile ? 2 : 3,
                ...customScrollbar,
                // PC版でのスクロール問題を修正
                height: '100%',
                minHeight: 0,
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 2,
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    p: 2, 
                    background: 'rgba(0, 255, 255, 0.05)', 
                    border: `1px solid rgba(0, 255, 255, 0.3)`,
                    borderRadius: 0,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.1), transparent)',
                      animation: 'scan 3s linear infinite',
                    },
                  }}>
                    <CalendarMonthIcon sx={{ color: theme.palette.neon.cyan }} />
                    <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1rem' }, letterSpacing: '0.05em' }}>
                      {selectedEvent.date ? new Date(selectedEvent.date).toLocaleDateString('ja-JP') : '未定'}
                    </Typography>
                  </Box>

                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    p: 2, 
                    background: 'rgba(255, 0, 255, 0.05)', 
                    border: `1px solid rgba(255, 0, 255, 0.3)`,
                    borderRadius: 0,
                  }}>
                    <AccessTimeIcon sx={{ color: theme.palette.neon.magenta }} />
                    <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1rem' }, letterSpacing: '0.05em' }}>
                      {formatTime(selectedEvent.time)}
                    </Typography>
                  </Box>

                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    p: 2, 
                    background: 'rgba(0, 255, 0, 0.05)', 
                    border: `1px solid rgba(0, 255, 0, 0.3)`,
                    borderRadius: 0,
                  }}>
                    <LocationOnIcon sx={{ color: theme.palette.neon.lime }} />
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1rem' }, letterSpacing: '0.05em' }}>
                        {selectedEvent.venue}
                      </Typography>
                      <IconButton
                        size="small"
                        sx={{ 
                          background: 'rgba(0, 255, 0, 0.1)',
                          border: `1px solid ${theme.palette.neon.lime}`,
                          color: theme.palette.neon.lime,
                          '&:hover': {
                            background: 'rgba(0, 255, 0, 0.2)',
                            boxShadow: `0 0 15px ${theme.palette.neon.lime}`,
                            transform: 'scale(1.1)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(
                            `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedEvent.venue + ' ' + selectedEvent.venueLocation)}`,
                            '_blank'
                          );
                        }}
                      >
                        <LocationOnIcon sx={{ fontSize: '1.2rem' }} />
                      </IconButton>
                    </Box>
                  </Box>

                  {selectedEvent.matchups && selectedEvent.matchups.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="h6" sx={{ 
                        fontSize: { xs: '1.1rem', sm: '1.2rem' }, 
                        mb: 2, 
                        fontWeight: 700, 
                        color: theme.palette.neon.cyan,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        textShadow: `0 0 10px ${theme.palette.neon.cyan}`,
                      }}>
                        対戦カード
                      </Typography>
                      {selectedEvent.matchups.map((matchup, index) => (
                        <Box key={index} sx={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          gap: 0.5,
                          mb: 2,
                          p: 2.5,
                          background: 'rgba(0, 255, 255, 0.02)',
                          borderRadius: 0,
                          border: `1px solid rgba(0, 255, 255, 0.2)`,
                          position: 'relative',
                          '&:hover': {
                            border: `1px solid rgba(0, 255, 255, 0.4)`,
                            background: 'rgba(0, 255, 255, 0.05)',
                            boxShadow: '0 0 20px rgba(0, 255, 255, 0.2)',
                          },
                          transition: 'all 0.3s ease',
                        }}>
                          <Box sx={{ 
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1.5
                          }}>
                            <Typography variant="subtitle2" sx={{ 
                              color: theme.palette.neon.magenta, 
                              fontSize: { xs: '0.85rem', sm: '0.9rem' }, 
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                            }}>
                              {matchup.round}
                            </Typography>
                            {matchup.boFormat && (
                              <Typography variant="subtitle2" sx={{ 
                                color: theme.palette.neon.lime, 
                                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                background: 'rgba(0, 255, 0, 0.1)',
                                border: `1px solid ${theme.palette.neon.lime}`,
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 0,
                                fontWeight: 700,
                              }}>
                                {matchup.boFormat}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            gap: 4
                          }}>
                            {matchup.teams.map((team, teamIndex) => (
                              <React.Fragment key={teamIndex}>
                                <Typography variant="body1" sx={{ 
                                  fontWeight: 800, 
                                  fontSize: { xs: '1.15rem', sm: '1.25rem' },
                                  color: '#ffffff',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em',
                                }}>
                                  {team.name}
                                </Typography>
                                {teamIndex === 0 && (
                                  <Typography variant="h6" sx={{ 
                                    fontWeight: 900, 
                                    fontSize: { xs: '1.1rem', sm: '1.2rem' },
                                    color: theme.palette.neon.cyan,
                                    textShadow: `0 0 15px ${theme.palette.neon.cyan}`,
                                  }}>
                                    VS
                                  </Typography>
                                )}
                              </React.Fragment>
                            ))}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}

                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ 
                      fontSize: { xs: '1.1rem', sm: '1.2rem' }, 
                      mb: 2, 
                      fontWeight: 700, 
                      color: theme.palette.neon.magenta,
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      textShadow: `0 0 10px ${theme.palette.neon.magenta}`,
                    }}>
                      配信情報
                    </Typography>
                    {selectedEvent.broadcast && selectedEvent.broadcast.length > 0 ? (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: 1.5
                      }}>
                        {selectedEvent.broadcast.map((stream, index) => (
                          <Box key={index} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            p: 2,
                            background: 'rgba(255, 0, 255, 0.05)',
                            borderRadius: 0,
                            border: `1px solid rgba(255, 0, 255, 0.3)`,
                            cursor: 'pointer',
                            '&:hover': {
                              borderColor: theme.palette.neon.magenta,
                              background: 'rgba(255, 0, 255, 0.1)',
                              boxShadow: `0 0 20px ${theme.palette.neon.magenta}`,
                              transform: 'translateX(5px)',
                            },
                            transition: 'all 0.3s ease',
                          }}>
                            <StreamIcon sx={{ color: theme.palette.neon.magenta }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body1" sx={{ 
                                color: '#ffffff', 
                                fontWeight: 600, 
                                fontSize: { xs: '0.95rem', sm: '1rem' },
                                letterSpacing: '0.05em',
                              }}>
                                {stream.platform}
                              </Typography>
                              <Typography 
                                component="a" 
                                href={stream.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                sx={{ 
                                  color: theme.palette.neon.cyan,
                                  textDecoration: 'none',
                                  fontWeight: 500,
                                  '&:hover': {
                                    textDecoration: 'underline',
                                    textShadow: `0 0 10px ${theme.palette.neon.cyan}`,
                                  }
                                }}
                              >
                                配信を見る
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        配信情報は未定です
                      </Typography>
                    )}
                  </Box>

                  {selectedEvent.officialSite && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" sx={{ 
                        fontSize: { xs: '1.1rem', sm: '1.2rem' }, 
                        mb: 2, 
                        fontWeight: 700, 
                        color: theme.palette.neon.lime,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        textShadow: `0 0 10px ${theme.palette.neon.lime}`,
                      }}>
                        公式サイト
                      </Typography>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        background: 'rgba(0, 255, 0, 0.05)',
                        borderRadius: 0,
                        border: `1px solid rgba(0, 255, 0, 0.3)`,
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: theme.palette.neon.lime,
                          background: 'rgba(0, 255, 0, 0.1)',
                          boxShadow: `0 0 20px ${theme.palette.neon.lime}`,
                          transform: 'translateX(5px)',
                        },
                        transition: 'all 0.3s ease',
                      }}>
                        <LanguageIcon sx={{ color: theme.palette.neon.lime }} />
                        <Typography 
                          component="a" 
                          href={selectedEvent.officialSite} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          sx={{ 
                            color: theme.palette.neon.lime,
                            textDecoration: 'none',
                            fontWeight: 600,
                            letterSpacing: '0.05em',
                            '&:hover': {
                              textDecoration: 'underline',
                              textShadow: `0 0 10px ${theme.palette.neon.lime}`,
                            }
                          }}
                        >
                          大会公式サイトを見る
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </AnimatePresence>
    </Modal>
  );
};

export default EventDetailModal;
